import React, { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as ROUTES from '../../constants/routes';
import useApi from '../../hooks/use-api';
import ErrorMessage from '../common/messages/ErrorMessage';

type DationIdentifier = {
	id: number;
	dationInstance: DationInstance;
};

type DationInstance = {
	id: number;
	name: string;
};

type Props = {
	show: boolean;
	setShow: (show: boolean) => void;
	companyId: string | number;
	dationIdentifiers: DationIdentifier[];
	dationInstances: DationInstance[];
	handleSubmit: () => void;
};

const LinkDrivingSchoolModal = ({
	show,
	setShow,
	companyId,
	dationIdentifiers,
	dationInstances,
	handleSubmit,
}: Props) => {
	const { t } = useTranslation();
	const [selectedInstanceId, setSelectedInstanceId] = useState('');
	const [isWorking, setWorking] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const [, postIdentifier] = useApi.post(ROUTES.postCompanyDationIdentifier(companyId));

	const linkedIds = new Set(dationIdentifiers.map((d) => d.dationInstance.id));
	const availableInstances = dationInstances.filter((instance) => !linkedIds.has(instance.id));

	const submit = () => {
		setWorking(true);
		postIdentifier({ dationInstanceId: parseInt(selectedInstanceId, 10) })
			.then(() => {
				handleSubmit();
				close();
			})
			.catch(() => {
				const selectedInstance = availableInstances.find(
					(i) => i.id === parseInt(selectedInstanceId, 10)
				);
				setErrorMessage(t('company.link_error', { name: selectedInstance?.name ?? '' }));
			})
			.finally(() => setWorking(false));
	};

	const close = () => {
		setSelectedInstanceId('');
		setErrorMessage(null);
		setShow(false);
	};

	return (
		<Modal show={show} onHide={close}>
			<Modal.Header closeButton>
				<Modal.Title>{t('company.link_driving_school')}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Form.Select
					value={selectedInstanceId}
					onChange={(e) => setSelectedInstanceId(e.target.value)}>
					<option value="">-</option>
					{availableInstances.map(({ id, name }) => (
						<option value={id} key={id}>
							{name}
						</option>
					))}
				</Form.Select>
				{errorMessage && <ErrorMessage message={errorMessage} />}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={close}>
					{t('add_employee.cancel')}
				</Button>
				<Button disabled={!selectedInstanceId || isWorking} onClick={submit}>
					{t('company.link_driving_school')}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default LinkDrivingSchoolModal;
